<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDateLocale } from 'dashboard/composables/useTimeAgo';
import LineChart from 'shared/components/charts/LineChart.vue';
import ReportCards from './ReportCards.vue';

const props = defineProps({
  report: { type: Object, default: null },
  loading: { type: Boolean, default: false },
});

const { t } = useI18n();
const { localeTag } = useDateLocale();
const numberFormatter = new Intl.NumberFormat(localeTag.value);
const formatNumber = v => numberFormatter.format(Number(v) || 0);

const cards = computed(() => {
  const c = props.report?.cards ?? {};
  return [
    {
      key: 'total',
      label: t('CAMPAIGN.REPORTS.OVERVIEW.CARDS.TOTAL'),
      value: formatNumber(c.total),
    },
    {
      key: 'sent',
      label: t('CAMPAIGN.REPORTS.OVERVIEW.CARDS.SENT'),
      value: formatNumber(c.sent),
    },
    {
      key: 'delivered',
      label: t('CAMPAIGN.REPORTS.OVERVIEW.CARDS.DELIVERED'),
      value: formatNumber(c.delivered),
    },
    {
      key: 'read',
      label: t('CAMPAIGN.REPORTS.OVERVIEW.CARDS.READ'),
      value: formatNumber(c.read),
    },
    {
      key: 'failed',
      label: t('CAMPAIGN.REPORTS.OVERVIEW.CARDS.FAILED'),
      value: formatNumber(c.failed),
    },
    {
      key: 'recipients',
      label: t('CAMPAIGN.REPORTS.OVERVIEW.CARDS.UNIQUE_RECIPIENTS'),
      value: formatNumber(c.unique_recipients),
    },
  ];
});

const lineData = computed(() => {
  const series = props.report?.daily_delivered ?? {};
  const labels = Object.keys(series).map(date => {
    const [, m, d] = date.split('-');
    return `${d}/${m}`;
  });
  const values = Object.values(series);
  return {
    labels,
    datasets: [
      {
        label: t('CAMPAIGN.REPORTS.OVERVIEW.CARDS.DELIVERED'),
        data: values,
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
      },
    ],
  };
});

const hasSeries = computed(() =>
  Object.values(props.report?.daily_delivered ?? {}).some(v => v > 0)
);
</script>

<template>
  <div class="flex flex-col gap-6">
    <ReportCards :cards="cards" :loading="loading" :skeleton-count="6" />
    <div
      class="border border-n-slate-4 bg-n-solid-1 rounded-lg p-4 flex flex-col gap-3"
    >
      <span class="text-sm font-semibold text-n-slate-12">
        {{ t('CAMPAIGN.REPORTS.OVERVIEW.DELIVERED_PER_DAY') }}
      </span>
      <div class="h-72">
        <div
          v-if="loading"
          class="h-full rounded-md bg-n-alpha-2 animate-pulse"
        />
        <LineChart v-else-if="hasSeries" :collection="lineData" />
        <div
          v-else
          class="h-full flex items-center justify-center text-sm text-n-slate-10"
        >
          {{ t('CAMPAIGN.REPORTS.NO_DATA') }}
        </div>
      </div>
    </div>
  </div>
</template>
